<script setup lang="ts">
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'

import { ViewCompactItemType } from '@lib/types/files'
import useAppStore from '@browser/stores/app'
import useApiStore from '@browser/stores/api'
import { useI18n } from 'vue-i18n'
import useThemeVars from '@lib/composables/useThemeVars'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'
import tooltipDirective from '@lib/directives/tooltip'
import { computed } from 'vue'
import type { MiniFolder as CompactFolder } from '@openapi'

const vTooltip = tooltipDirective
const { t } = useI18n()
const themeVars = useThemeVars()
const { parseNameOnly } = useFolderNameDescription()

const { selectedItemIds, selectedSection, resetSelectedItems, removeSelectedItems, displayedFiles } = useAppStore()
const { state: apiState } = useApiStore()

const selectedFiles = computed(() => {
  if (selectedSection.value === 'all') {
    return displayedFiles.value
      .filter((file) => {
        return selectedItemIds.value.includes(file.id)
      })
      .map((file) => ({
        ...file,
        type: ViewCompactItemType.File,
      }))
  }
  return []
})

const selectedFolders = computed(() => {
  if (selectedSection.value === 'all') {
    return apiState.folders
      .filter((folder: CompactFolder) => {
        return selectedItemIds.value.includes(folder.id)
      })
      .map((folder: CompactFolder) => ({
        ...folder,
        thumbnail_url: undefined,
        type: ViewCompactItemType.Folder,
      }))
  }
  return []
})

const allSelectedItems = computed(() => {
  if (selectedSection.value === 'all') {
    return [...selectedFolders.value, ...selectedFiles.value]
  } else if (selectedSection.value === 'favorites') {
    return Object.keys(apiState.favorites)
      .filter((favoriteId) => {
        return selectedItemIds.value.includes(favoriteId)
      })
      .map((favoriteId) => {
        const favorite = apiState.favorites[favoriteId]
        return {
          ...favorite,
          thumbnail_url: null,
          id: favoriteId,
          type: favorite.folder ? ViewCompactItemType.Folder : ViewCompactItemType.File,
        }
      })
  } else if (selectedSection.value === 'recent') {
    return apiState.recent
      .filter((recent) => selectedItemIds.value.includes(recent.id))
      .map((f) => ({ ...f, type: ViewCompactItemType.File }))
  }

  return []
})

function getDisplayName(item: any): string {
  if (item.type === ViewCompactItemType.Folder) {
    return parseNameOnly(item.name || '')
  }
  return item.name || ''
}
</script>

<template>
  <div class="items-panel flex flex-col items-center gap-2">
    <div class="items-panel_header pb-2 flex justify-between items-center w-full sticky top-0 z-1">
      <div class="font-600">
        {{
          t('canvasUI.browserApp.itemsPanel.selected', {
            selectionIndicator: `${selectedItemIds.length}`,
          })
        }}
      </div>
      <CButton @click="resetSelectedItems">
        {{ t('canvasUI.browserApp.itemsPanel.clearSelection') }}
      </CButton>
    </div>
    <div class="flex flex-col gap-2 w-full">
      <div
        v-for="item in allSelectedItems"
        :key="item.id"
        class="flex items-center justify-between p-2 gap-1 h-[62px] border-rd-1 items-panel_item"
      >
        <div class="flex items-center min-w-0">
          <div v-if="item.type === ViewCompactItemType.Folder" class="w-[53px] flex items-center justify-center mr-3">
            <CIcon class="items-panel_item-folder-icon" fa-type="fas" icon="folder" size="24px" />
          </div>

          <CImage
            v-else-if="item?.thumbnail_url"
            class="rounded-2 mr-3"
            height="46"
            :img-props="{
              draggable: false,
            }"
            lazy
            object-fit="cover"
            preview-disabled
            :src="item.thumbnail_url"
            width="53"
          />
          <div class="font-700 truncate flex-1 min-w-0">
            <span class="truncate" style="max-width: calc(100% - 53px)">{{ getDisplayName(item) }}</span>
          </div>
        </div>
        <CButton
          v-tooltip="t('canvasUI.browserApp.itemsPanel.deselect')"
          circle
          :color="themeVars.primary5"
          :text-color="themeVars.primary"
          @click="removeSelectedItems([item.id])"
        >
          <CIcon fa-type="fas" icon="close" />
        </CButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.items-panel {
  &_header {
    background-color: v-bind('themeVars.base');
  }
  &_item {
    border: 0px solid v-bind('themeVars.primary5');
    background-color: v-bind('themeVars.primary6');
  }
  &_item-folder-icon {
    color: v-bind('themeVars.primary3');
  }
}
</style>
